import { useEffect, useState } from 'react';

/**
 * Global "current project" scope (ADR-0029).
 *
 * Backed by localStorage (so a brand selection survives navigation — each
 * route re-mounts AppShell — and page reloads) plus a window CustomEvent so
 * every mounted consumer updates live when the switcher changes it. Empty
 * string '' means "All projects".
 *
 * Deliberately not a React context: the value must outlive the per-route
 * AppShell remount, and a tiny localStorage-backed hook is simpler than
 * threading a provider above the router.
 */
const KEY = 'markaii.projectScope';
const EVT = 'markaii:project-scope';

export function getProjectScope() {
  try { return localStorage.getItem(KEY) || ''; } catch { return ''; }
}

export function setProjectScope(value) {
  try {
    if (value) localStorage.setItem(KEY, value);
    else localStorage.removeItem(KEY);
  } catch { /* ignore quota/availability */ }
  window.dispatchEvent(new CustomEvent(EVT, { detail: value || '' }));
}

export function useProjectScope() {
  const [scope, setScope] = useState(getProjectScope);
  useEffect(() => {
    const onEvt = (e) => setScope(e.detail || '');
    const onStorage = (e) => { if (e.key === KEY) setScope(e.newValue || ''); };
    window.addEventListener(EVT, onEvt);
    window.addEventListener('storage', onStorage); // cross-tab sync
    return () => {
      window.removeEventListener(EVT, onEvt);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return [scope, setProjectScope];
}
